import { match } from "ts-pattern";
import type { Except } from "type-fest";

import type { FormElement } from "../collectors/form/plainForm.js";
import type { Assessment } from "../types/assessment.js";
import {
  callsForAction,
  categoriesOf,
  ratedItemsOf,
} from "./assessmentSummary.js";
import type { GroupCategory } from "./authoring.js";
import type { JsonValue } from "./document.js";
import { isNotAnswered } from "./notAnswered.js";
import type { PublishedInstrumentSchema } from "./publishedSchema.js";
import { scaleOf, type RatingScale } from "./ratingScale.js";
import { commentOf, responseTextOf } from "./responseText.js";

type Response = Assessment["submissions"][number]["responses"][number];
type ItemElement = Extract<FormElement, { kind: "item" }>;

/** One rating an item offers, as our form words it. */
export type RatingShown = {
  value: string | number;
  title: string;
  description?: string | undefined;
};

/** One item sent, as the full assessment shows it. */
export type FullItem = {
  group: string;
  item: string;
  /** Its domain's number and its own, as `3.2`. */
  number: string;
  title: string;
  description?: string | undefined;
  /** The rating given, where the item offers ratings and the answer is one of them. */
  rating?: RatingShown | undefined;
  /** The answer as it was stored, where it is neither one of the item's ratings nor not answered. */
  answer?: string | undefined;
  /** Whether it was sent not answered, so holds neither a rating nor an answer. */
  notAnswered: boolean;
  /** Every rating the item offers, in our form's order; empty for a free answer. */
  ratings: readonly RatingShown[];
  scale: RatingScale;
  /** `null` where nothing was said. */
  comment: string | null;
  /** Whether the rating calls for action in a plan; see `callsForAction`. */
  action: boolean;
  /** Whether the full history reads this answer as a rating, being one returned by `ratedItemsOf`. */
  inFullHistory: boolean;
};

export type FullDomain = {
  group: string;
  number: number;
  /** Absent where the instrument does not say what kind of domain it is. */
  category?: GroupCategory | undefined;
  title: string;
  description?: string | undefined;
  items: readonly FullItem[];
  /** How many of its items were sent not answered. */
  notAnsweredCount: number;
};

export type FullAssessmentView = {
  domains: readonly FullDomain[];
  /** Whether the instrument says what kind each domain is, without which nothing is an action item. */
  categorised: boolean;
  itemCount: number;
  actionCount: number;
  commentCount: number;
  /** How many items were sent not answered; every other item is answered. */
  notAnsweredCount: number;
};

/** Which items the full assessment can list, in the order they are offered. */
export const FULL_FILTERS = [
  "all",
  "action",
  "comment",
  "not_answered",
] as const;

export type FullFilter = (typeof FULL_FILTERS)[number];

const ratingsOf = (element: ItemElement | undefined): RatingShown[] => {
  const control = element?.presentation.control;

  return control?.kind === "choice"
    ? control.options.map(({ value, title, description }) => ({
        value,
        title: title.text,
        description: description?.text,
      }))
    : [];
};

const isAction = ({
  category,
  response,
}: {
  category: GroupCategory | undefined;
  response: JsonValue;
}): boolean =>
  category !== undefined &&
  typeof response === "number" &&
  callsForAction({ category, rating: response });

/**
 * A submission laid out as our form asks it, every item it holds with its
 * words, the rating given or that it was not answered, every rating it might
 * have been, and its comment.
 *
 * A response the form has no element for is still listed, after the form's
 * own items and titled by its key, since a submission is a record and nothing
 * in it may go unseen. An instrument declaring no categories has no action
 * items.
 */
export const fullAssessmentOf = ({
  schema,
  form,
  responses,
}: {
  schema: PublishedInstrumentSchema;
  form: readonly FormElement[];
  responses: readonly Response[];
}): FullAssessmentView => {
  const categories = categoriesOf(schema);
  const ratedKeys = new Set(
    (ratedItemsOf({ schema, form, responses }) ?? []).map(
      ({ group, item }) => `${group}/${item}`,
    ),
  );
  const byKey = new Map(
    responses.map((response) => [
      `${response.group}/${response.item}`,
      response,
    ]),
  );
  const domains = new Map<
    string,
    Except<FullDomain, "number" | "items" | "notAnsweredCount"> & {
      items: Omit<FullItem, "number">[];
    }
  >();

  const domainFor = (group: string) => {
    const known = domains.get(group);

    if (known !== undefined) {
      return known;
    }

    const heading = form.find(
      (element) => element.kind === "group" && element.at.group === group,
    );
    const domain = {
      group,
      category: categories?.get(group),
      title: heading?.presentation.title.text ?? group,
      description: heading?.presentation.description?.text,
      items: [],
    };
    domains.set(group, domain);
    return domain;
  };

  const add = (
    response: Response,
    element?: ItemElement,
    comment?: Response,
  ) => {
    const ratings = ratingsOf(element);
    const rating = ratings.find(({ value }) => value === response.response);
    const notAnswered = isNotAnswered(response.response);

    domainFor(response.group).items.push({
      group: response.group,
      item: response.item,
      title: element?.presentation.title.text ?? response.item,
      description: element?.presentation.description?.text,
      ...(rating === undefined && !notAnswered
        ? { answer: responseTextOf(response.response) }
        : {}),
      ...(rating === undefined ? {} : { rating }),
      notAnswered,
      ratings,
      scale: scaleOf(ratings.map(({ value }) => value)),
      comment: commentOf(comment),
      action: isAction({
        category: categories?.get(response.group),
        response: response.response,
      }),
      inFullHistory: ratedKeys.has(`${response.group}/${response.item}`),
    });
  };

  // Every domain our form asks takes its place in the form's order before any
  // item is added; one left with no item is dropped below.
  for (const element of form) {
    if (element.kind === "group") {
      domainFor(element.at.group);
    }
  }

  for (const element of form) {
    if (element.kind !== "item") {
      continue;
    }

    const key = `${element.at.group}/${element.at.item}`;
    const response = byKey.get(key);
    const commentKey =
      element.commentAt === undefined
        ? undefined
        : `${element.commentAt.group}/${element.commentAt.item}`;

    if (response !== undefined) {
      add(
        response,
        element,
        commentKey === undefined ? undefined : byKey.get(commentKey),
      );
      byKey.delete(key);

      if (commentKey !== undefined) {
        byKey.delete(commentKey);
      }
    }
  }

  for (const unplaced of byKey.values()) {
    add(unplaced);
  }

  const numbered = [...domains.values()]
    .filter(({ items }) => items.length > 0)
    .map((domain, index): FullDomain => ({
      ...domain,
      number: index + 1,
      items: domain.items.map((each, position) => ({
        ...each,
        number: `${String(index + 1)}.${String(position + 1)}`,
      })),
      notAnsweredCount: domain.items.filter(({ notAnswered }) => notAnswered)
        .length,
    }));
  const items = numbered.flatMap((domain) => domain.items);

  return {
    domains: numbered,
    categorised: categories !== undefined,
    itemCount: items.length,
    actionCount: items.filter(({ action }) => action).length,
    commentCount: items.filter(({ comment }) => comment !== null).length,
    notAnsweredCount: items.filter(({ notAnswered }) => notAnswered).length,
  };
};

/** The items of a domain a filter lists. */
export const itemsShown = ({
  domain,
  filter,
}: {
  domain: FullDomain;
  filter: FullFilter;
}): readonly FullItem[] =>
  match(filter)
    .with("all", () => domain.items)
    .with("action", () => domain.items.filter(({ action }) => action))
    .with("comment", () =>
      domain.items.filter(({ comment }) => comment !== null),
    )
    .with("not_answered", () =>
      domain.items.filter(({ notAnswered }) => notAnswered),
    )
    .exhaustive();
